export interface Coordinates {
  latitude: number;
  longitude: number;
  accuracy: number;
}

export type GeolocationFailureReason = "denied" | "unavailable" | "timeout" | "unsupported";

const MESSAGES: Record<GeolocationFailureReason, string> = {
  denied: "Location access is required to mark attendance. Please enable location permission and try again.",
  unavailable: "Unable to determine your current location. Please enable GPS/location services and try again.",
  timeout: "Getting your location took too long. Please check your GPS/network signal and try again.",
  unsupported: "Your browser does not support location capture, so attendance cannot be marked from this device.",
};

export class GeolocationCaptureError extends Error {
  reason: GeolocationFailureReason;
  constructor(reason: GeolocationFailureReason) {
    super(MESSAGES[reason]);
    this.name = "GeolocationCaptureError";
    this.reason = reason;
  }
}

/**
 * The accuracy (in metres) a reading has to reach before we stop waiting for
 * a better one. This is NOT a validation threshold and nothing is rejected
 * for missing it - the backend owns that decision, against the tenant's own
 * configured `max_location_accuracy_meters` and the branch geo-fence. It is
 * purely "good enough to stop asking the GPS chip for more".
 */
export const TARGET_ACCURACY_METERS = 40;

/** How long to keep improving a poor first fix before submitting the best one. */
export const ACQUISITION_TIMEOUT_MS = 15_000;

interface CaptureOptions {
  /** Stop as soon as a reading is at least this accurate. */
  targetAccuracyMeters?: number;
  /** Hard ceiling on the whole acquisition, refinement included. */
  timeoutMs?: number;
}

/**
 * Captures the device's CURRENT position, refining a poor first fix instead
 * of submitting it.
 *
 * The previous implementation took a single `getCurrentPosition` reading and
 * sent whatever came back. That is the root of the "Accuracy is too low"
 * reports: the first fix a browser hands over is very often the coarse
 * network/Wi-Fi estimate (hundreds or thousands of metres) that arrives
 * while the GPS chip is still warming up, and a phone sitting on a desk in
 * the office would be refused on the strength of it - even though a second
 * or two later the same device reports a perfectly usable ±10m fix.
 *
 * So this watches the position stream instead and keeps the BEST reading
 * seen, resolving as soon as one is accurate enough (`targetAccuracyMeters`)
 * or when the acquisition window closes, whichever comes first. Practically:
 *
 *     get a reading -> accurate enough? submit it
 *                   -> too coarse?      keep watching, keep the best
 *                   -> window closed?   submit the best one seen
 *
 * What it deliberately does NOT do (see the spec's section 6 and 22):
 *   - it never falls back to a cached fix as the primary reading
 *     (`maximumAge: 0`);
 *   - it never invents coordinates, and never substitutes the branch/office
 *     location for the employee's;
 *   - it never turns a permission denial, a disabled GPS or a timeout into a
 *     successful punch - each surfaces as its own `GeolocationCaptureError`
 *     reason, so the UI can say what actually went wrong.
 *
 * Validation of the resulting coordinates - accuracy threshold and geo-fence
 * alike - stays entirely server-side.
 */
export function captureCurrentLocation(options: CaptureOptions = {}): Promise<Coordinates> {
  const targetAccuracy = options.targetAccuracyMeters ?? TARGET_ACCURACY_METERS;
  const timeoutMs = options.timeoutMs ?? ACQUISITION_TIMEOUT_MS;

  return new Promise((resolve, reject) => {
    if (typeof navigator === "undefined" || !navigator.geolocation) {
      reject(new GeolocationCaptureError("unsupported"));
      return;
    }

    let best: Coordinates | null = null;
    let settled = false;
    let watchId: number | null = null;
    let timer: ReturnType<typeof setTimeout> | null = null;

    const cleanup = () => {
      if (watchId !== null) navigator.geolocation.clearWatch(watchId);
      if (timer !== null) clearTimeout(timer);
      watchId = null;
      timer = null;
    };

    const succeed = (coords: Coordinates) => {
      if (settled) return;
      settled = true;
      cleanup();
      resolve(coords);
    };

    const fail = (reason: GeolocationFailureReason) => {
      if (settled) return;
      // A reading we already hold beats an error that arrived afterwards:
      // once the device has told us where it is, a later timeout on the
      // refinement attempt is not a failure to locate the employee.
      if (best) {
        succeed(best);
        return;
      }
      settled = true;
      cleanup();
      reject(new GeolocationCaptureError(reason));
    };

    // The acquisition window is our own, independent of the per-reading
    // `timeout` below: it bounds the whole refine-the-fix loop so a device
    // that keeps reporting coarse positions still submits its best one
    // rather than leaving the employee waiting indefinitely.
    timer = setTimeout(() => {
      if (best) succeed(best);
      else fail("timeout");
    }, timeoutMs);

    watchId = navigator.geolocation.watchPosition(
      (position) => {
        const reading: Coordinates = {
          latitude: position.coords.latitude,
          longitude: position.coords.longitude,
          // Some browsers report no accuracy at all. Treat that as "unknown,
          // and therefore not better than anything we already have" rather
          // than inventing a number - the backend accepts a missing accuracy
          // and falls back to the distance check alone.
          accuracy: typeof position.coords.accuracy === "number" ? position.coords.accuracy : Number.POSITIVE_INFINITY,
        };
        if (!best || reading.accuracy < best.accuracy) {
          best = reading;
        }
        if (best.accuracy <= targetAccuracy) {
          succeed(best);
        }
      },
      (error) => {
        if (error.code === error.PERMISSION_DENIED) {
          // A denial is never salvageable by a partial reading - the user
          // has to grant permission - so it short-circuits even if an
          // earlier fix somehow arrived.
          if (!settled) {
            settled = true;
            cleanup();
            reject(new GeolocationCaptureError("denied"));
          }
          return;
        }
        if (error.code === error.TIMEOUT) {
          fail("timeout");
          return;
        }
        fail("unavailable");
      },
      { enableHighAccuracy: true, timeout: timeoutMs, maximumAge: 0 },
    );
  });
}
